import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, FileSignature, FileClock, FileWarning, CalendarClock } from 'lucide-react';
import { format, differenceInCalendarDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useContratosMantenimiento } from '@/hooks/useContratosMantenimiento';
import { useSecurityContext } from '@/contexts/MaintenanceContext';

const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador',
  pendiente_firma: 'Pendiente de firma',
  vigente: 'Vigente',
  vencido: 'Vencido',
  rescindido: 'Rescindido',
};

const estadoClass = (estado: string) => {
  switch (estado) {
    case 'vigente': return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';
    case 'pendiente_firma': return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30';
    case 'vencido': return 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30';
    case 'rescindido': return 'bg-muted text-muted-foreground border-border';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const PERIODICIDAD: Record<string, string> = {
  anual: 'Anual', semestral: 'Semestral', trimestral: 'Trimestral', mensual: 'Mensual',
};

const fmt = (d?: string | null) => (d ? format(new Date(d), "d MMM yyyy", { locale: es }) : '—');

export const ContratosResumen: React.FC = () => {
  const navigate = useNavigate();
  const { contratos, loading } = useContratosMantenimiento();
  const { clientes } = useSecurityContext();
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState('todos');

  const nombreCliente = useMemo(() => {
    const m = new Map<string, string>();
    (clientes || []).forEach((c: any) => m.set(c.id, c.nombre));
    return m;
  }, [clientes]);

  const activos = useMemo(() => contratos.filter(c => !c.archivado), [contratos]);
  const hoy = new Date();

  const resumen = useMemo(() => {
    let vigentes = 0, pendientes = 0, vencidos = 0, caducanPronto = 0;
    activos.forEach(c => {
      if (c.estado === 'vigente') vigentes += 1;
      if (c.estado === 'pendiente_firma' || c.estado === 'borrador') pendientes += 1;
      if (c.estado === 'vencido') vencidos += 1;
      if (c.estado === 'vigente' && c.fecha_fin) {
        const dias = differenceInCalendarDays(new Date(c.fecha_fin), hoy);
        if (dias >= 0 && dias <= 60) caducanPronto += 1;
      }
    });
    return { vigentes, pendientes, vencidos, caducanPronto };
  }, [activos]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return activos
      .filter(c => estado === 'todos' || c.estado === estado)
      .filter(c => {
        if (!q) return true;
        return (c.numero_formateado || '').toLowerCase().includes(q)
          || (nombreCliente.get(c.cliente_id) || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.fecha_fin || '').localeCompare(b.fecha_fin || ''));
  }, [activos, estado, busqueda, nombreCliente]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const tarjetas = [
    { label: 'Vigentes', value: resumen.vigentes, icon: FileSignature, color: 'text-emerald-500' },
    { label: 'Pendientes de firma', value: resumen.pendientes, icon: FileClock, color: 'text-amber-500' },
    { label: 'Caducan en 60 días', value: resumen.caducanPronto, icon: CalendarClock, color: 'text-orange-500' },
    { label: 'Vencidos', value: resumen.vencidos, icon: FileWarning, color: 'text-red-500' },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {tarjetas.map(t => {
          const Icon = t.icon;
          return (
            <Card key={t.label}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-xs font-medium">{t.label}</CardTitle>
                <Icon className={`h-4 w-4 ${t.color}`} />
              </CardHeader>
              <CardContent><div className="text-2xl font-bold">{t.value}</div></CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar por cliente o número de contrato..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
        </div>
        <Select value={estado} onValueChange={setEstado}>
          <SelectTrigger className="md:w-56"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            {Object.entries(ESTADO_LABEL).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {filtrados.length} contrato{filtrados.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {filtrados.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No hay contratos que coincidan con los filtros.
            </p>
          )}
          {filtrados.map(c => (
            <button
              key={c.id}
              onClick={() => navigate(`/cliente/${c.cliente_id}`)}
              className="w-full text-left rounded-lg border p-3 hover:bg-accent/50 transition-colors min-h-[44px]"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-xs text-muted-foreground shrink-0">{c.numero_formateado}</span>
                  <span className="font-medium truncate">{nombreCliente.get(c.cliente_id) || 'Cliente'}</span>
                </div>
                <Badge variant="outline" className={estadoClass(c.estado)}>
                  {ESTADO_LABEL[c.estado] ?? c.estado}
                </Badge>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{fmt(c.fecha_inicio)} → {fmt(c.fecha_fin)}</span>
                <span>{Number(c.precio_total || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                <span>{PERIODICIDAD[c.periodicidad_pago] ?? c.periodicidad_pago}</span>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};
